import { localizationClient } from '@lib/localizationClient';
import type { LanguagesResponse, StringBundleResponse } from '@app-types/localization';

/** Reserved namespace holding the existing platform strings. */
const NAMESPACE = 'V4_PLATFORM_WEB';

/**
 * Fetch the string bundle for a given language. ds-external always calls
 * without `organization_id` — tenant overrides do not apply on the external
 * signing/review surface (see design spec, 2026-05-26, § "Bundle scope").
 */
export const getStringBundle = async (language: string): Promise<Record<string, string>> => {
  const response = await localizationClient.post<StringBundleResponse>('/v2/strings/bundle', {
    namespace: NAMESPACE,
    languages: [language],
    send_all_keys: true,
  });
  // v2 returns `resolved` keyed by language; flatten to the requested language
  // so callers keep the flat `Record<string,string>` contract.
  return response.data.resolved[language] ?? {};
};

/** Fetch the list of available languages.
 *
 * Backend has shipped two shapes in different deploys: a bare `string[]`
 * and an object envelope `{ languages: string[] }`. Normalise both into a
 * plain array here so callers always see the same type. Anything else
 * (null, unrelated object, single string) yields an empty list — the
 * LocaleSwitcher hides itself when the list has 0 or 1 entry. */
export const getAvailableLanguages = async (): Promise<string[]> => {
  const response = await localizationClient.get<LanguagesResponse | { languages?: string[] }>(
    `/v2/languages?namespace=${NAMESPACE}`,
  );
  const data = response.data as unknown;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray((data as { languages?: unknown }).languages)) {
    return (data as { languages: string[] }).languages;
  }
  return [];
};
